import React, { useEffect, useMemo, useRef, useState } from 'react';

const monthLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (value) => String(value).padStart(2, '0');

const formatDisplay = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return '';
  return `${month}/${day}/${year}`;
};

const parseDisplay = (value) => {
  if (!value) return '';
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return '';
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (!month || !day || !year) return '';
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return '';
  return `${year}-${pad(month)}-${pad(day)}`;
};

const toViewDate = (value) => {
  if (!value) return new Date();
  const [year, month, day] = value.split('-').map((part) => Number(part));
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, 1);
};

export default function DateSelect({
  value = '',
  onChange,
  startYear,
  endYear,
  className = '',
  id,
  allowClear = true
}) {
  const now = new Date();
  const yearStart = startYear ?? now.getFullYear() - 100;
  const yearEnd = endYear ?? now.getFullYear() + 10;
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatDisplay(value));
  const [viewDate, setViewDate] = useState(() => toViewDate(value));

  useEffect(() => {
    setInputValue(formatDisplay(value));
    if (value) {
      setViewDate(toViewDate(value));
    }
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (!containerRef.current || containerRef.current.contains(event.target)) return;
      setOpen(false);
      setInputValue(formatDisplay(value));
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, value]);

  const years = useMemo(() => {
    const list = [];
    for (let y = yearStart; y <= yearEnd; y += 1) {
      list.push(String(y));
    }
    return list;
  }, [yearStart, yearEnd]);

  const month = viewDate.getMonth();
  const year = viewDate.getFullYear();
  const startDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const selectedValue = value || '';
  const selectedDay = selectedValue ? Number(selectedValue.split('-')[2]) : null;
  const selectedMonth = selectedValue ? Number(selectedValue.split('-')[1]) - 1 : null;
  const selectedYear = selectedValue ? Number(selectedValue.split('-')[0]) : null;

  const days = Array.from({ length: 42 }, (_, index) => {
    const dayNumber = index - startDay + 1;
    if (dayNumber < 1 || dayNumber > daysInMonth) return null;
    return dayNumber;
  });

  const handleSelectDay = (dayNumber) => {
    const nextValue = `${year}-${pad(month + 1)}-${pad(dayNumber)}`;
    onChange?.(nextValue);
    setInputValue(formatDisplay(nextValue));
    setOpen(false);
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    const parsed = parseDisplay(nextValue);
    if (parsed) {
      onChange?.(parsed);
      setViewDate(toViewDate(parsed));
    }
  };

  const clearValue = () => {
    setInputValue('');
    onChange?.('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`sp-date-select ${className}`}>
      <input
        id={id}
        className="sp-input sp-date-input"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        placeholder="MM/DD/YYYY"
        inputMode="numeric"
      />
      {allowClear && value && (
        <button className="sp-clear" type="button" onClick={clearValue}>
          Clear
        </button>
      )}
      {open && (
        <div className="sp-date-popover" role="dialog" aria-label="Choose date">
          <div className="sp-calendar-header">
            <button
              className="sp-calendar-nav"
              type="button"
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
            >
              ‹
            </button>
            <div className="sp-calendar-title">
              <span>{monthLabels[month]}</span>
              <select
                className="sp-select sp-select--sm"
                value={String(year)}
                onChange={(event) => setViewDate(new Date(Number(event.target.value), month, 1))}
              >
                {years.map((yearOption) => (
                  <option key={yearOption} value={yearOption}>
                    {yearOption}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="sp-calendar-nav"
              type="button"
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
            >
              ›
            </button>
          </div>
          <div className="sp-calendar-weekdays">
            {weekdayLabels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
          <div className="sp-calendar-grid">
            {days.map((dayNumber, index) => {
              if (!dayNumber) {
                return <span key={`empty-${index}`} className="sp-calendar-day sp-calendar-day--empty" />;
              }
              const isSelected =
                selectedDay === dayNumber && selectedMonth === month && selectedYear === year;
              return (
                <button
                  key={`${year}-${month}-${dayNumber}`}
                  type="button"
                  className={`sp-calendar-day${isSelected ? ' sp-calendar-day--selected' : ''}`}
                  onClick={() => handleSelectDay(dayNumber)}
                >
                  {dayNumber}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
